/**
 * MacroManager - Executes campaign macros (onLaunch, onJoin, onExit, onInactive)
 *
 * Responsibilities:
 * - Execute onLaunchMacro when game launches (awaited before matchmakeable)
 * - Execute onJoinMacro when match found (awaited before HOST_READY / stream start)
 * - Execute onExitMacro when all players leave with no restart (awaited before pool re-entry)
 * - Execute onInactiveMacro on a 30s interval while no players are connected
 * - Parse macro strings and send appropriate IPC messages to Playjector
 *
 * Macro Format:
 * - `key=<keyCode>@<timing>^<hold>` - Press a keyboard key
 * - `string=<text>@<timing>` - Type a string character-by-character
 * - `gpad=<button>@<timing>^<hold>` - Press a gamepad button (named: A, B, X, Y, DUP, LB, RT, etc.)
 *
 * Modifiers (optional):
 * - `@<ms>` - Delay before executing the step (default: 100ms)
 * - `^<ms>` - How long to hold the key/button (default: 20ms)
 *
 * Examples:
 * - `['key=121', 'string=LoadMap(SOH_LDCoop_Prototype)@1000', 'key=13@200^20']`
 * - `['gpad=A@100^20']` - Press gamepad A button
 */

import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import { generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../../../api/messaging/protocols/ipc';
import type { PlaycastGamepadXInput, PlaycastKeyDetail, XInput } from '@playcastdotio/CoreTypes';
import * as keycode from 'keyboard-key';
import type { IHostAnalyticsManager } from '../../../services/analytics/IHostAnalyticsManager';

// ========================================
// Macro Parsing
// ========================================

type ParsedMacroStep = {
  type: 'key' | 'string' | 'gpad';
  value: string;
  timing: number;
  hold: number;
};

const DEFAULT_TIMING_MS = 100;
const DEFAULT_HOLD_MS_KEYBOARD = 20;
// Gamepad presses default to a longer hold than keyboard presses because
// most games reject sub-frame controller inputs (a 20ms tap is ~1.2 frames
// at 60Hz and frequently gets debounced by the engine's input layer). 150ms
// gives ~9 frames of sustained pressure — past every reasonable game-side
// debounce window — and still feels like a deliberate single press, not a
// hold-action. Authors who need a shorter or longer tap can specify ^N.
const DEFAULT_HOLD_MS_GAMEPAD = 150;

/**
 * Parse modifier suffixes from a macro value string.
 * - `@<number>` = timing (delay before action), default 100ms
 * - `^<number>` = hold (key/button hold duration); default 20ms for
 *   key/string macros, 150ms for gpad macros (override per-call via
 *   `defaultHold`).
 */
function parseMacroString(
  raw: string,
  defaultHold: number = DEFAULT_HOLD_MS_KEYBOARD,
): { value: string; timing: number; hold: number } {
  const at = raw.indexOf('@');
  const caret = raw.indexOf('^');

  let endIdx = raw.length;
  if (at !== -1 && caret !== -1) endIdx = Math.min(at, caret);
  else if (at !== -1) endIdx = at;
  else if (caret !== -1) endIdx = caret;

  const value = raw.slice(0, endIdx);

  const timingMatch = raw.match(/@(\d+)/);
  const holdMatch = raw.match(/\^(\d+)/);

  const timing = timingMatch ? Number(timingMatch[1]) : DEFAULT_TIMING_MS;
  const hold = holdMatch ? Number(holdMatch[1]) : defaultHold;

  return { value, timing, hold };
}

/**
 * Parse a single macro step string into a typed command.
 *
 * Supported formats:
 * - `key=<keyCode>[@timing][^hold]`
 * - `string=<text>[@timing]`
 * - `gpad=<button>[@timing][^hold]`
 *
 * `gpad=` macros use a longer default hold (`DEFAULT_HOLD_MS_GAMEPAD`) than
 * `key=`/`string=` because games frequently reject sub-frame controller taps.
 */
function parseMacroStep(step: string): ParsedMacroStep | null {
  if (step.startsWith('key=')) {
    return { type: 'key', ...parseMacroString(step.substring(4)) };
  }

  if (step.startsWith('string=')) {
    return { type: 'string', ...parseMacroString(step.substring(7)) };
  }

  if (step.startsWith('gpad=')) {
    return { type: 'gpad', ...parseMacroString(step.substring(5), DEFAULT_HOLD_MS_GAMEPAD) };
  }

  console.warn('[MacroManager] Unknown macro step:', step);
  return null;
}

// ========================================
// Input Helpers
// ========================================

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function createKeyDetail(keyCode: number, shift: boolean): PlaycastKeyDetail {
  return {
    keyCode,
    location: 'standard',
    source: { type: 'keyboard', code: keyCode },
    modifiers: { ctrl: false, alt: false, shift, meta: false },
    displayName: 'host',
  };
}

function sendKeyDown(keyCode: number, shift: boolean): void {
  ipcSend(generators.keyboard.down('host', createKeyDetail(keyCode, shift)));
}

function sendKeyUp(keyCode: number, shift: boolean): void {
  ipcSend(generators.keyboard.up('host', createKeyDetail(keyCode, shift)));
}

async function sendKeyPress(keyCode: number, shift: boolean, hold: number): Promise<void> {
  sendKeyDown(keyCode, shift);
  await delay(hold);
  sendKeyUp(keyCode, shift);
  await delay(20);
}

// ========================================
// String Typing
// ========================================

const SHIFT_REQUIRED_CHARS = new Set([
  ')', '!', '@', '#', '$', '%', '^', '&', '*', '(',
  '_', '+', '{', '}', ':', '"', '<', '>', '?', '|',
]);

async function sendKeyString(text: string): Promise<void> {
  for (const char of text) {
    const code = keycode.getCode(char);
    if (!code) continue;

    const shift =
      (char === char.toUpperCase() && char !== char.toLowerCase()) ||
      SHIFT_REQUIRED_CHARS.has(char);

    if (shift) {
      sendKeyDown(16, shift);
      await delay(20);
    }

    await sendKeyPress(code, shift, 20);

    if (shift) {
      sendKeyUp(16, shift);
      await delay(20);
    }
  }
}

// ========================================
// Gamepad (XInput)
// ========================================

function toXInputButtonMask(button: string): number {
  switch (button) {
    case 'A': return 0x1000;
    case 'B': return 0x2000;
    case 'X': return 0x4000;
    case 'Y': return 0x8000;
    case 'DUP': return 0x0001;
    case 'DDOWN': return 0x0002;
    case 'DLEFT': return 0x0004;
    case 'DRIGHT': return 0x0008;
    case 'START': return 0x0010;
    case 'BACK': return 0x0020;
    case 'LS': return 0x0040;
    case 'RS': return 0x0080;
    case 'LB': return 0x0100;
    case 'RB': return 0x0200;
    case 'GUIDE': return 0x0400;
    default: return 0;
  }
}

function neutralXInput(): XInput {
  return {
    wButtons: 0,
    bLeftTrigger: 0,
    bRightTrigger: 0,
    sThumbLX: 0,
    sThumbLY: 0,
    sThumbRX: 0,
    sThumbRY: 0,
  };
}

function applyButtonToXInput(button: string, x: XInput): XInput {
  const mask = toXInputButtonMask(button);
  if (mask) return { ...x, wButtons: x.wButtons | mask };

  switch (button) {
    case 'LT': return { ...x, bLeftTrigger: 255 };
    case 'RT': return { ...x, bRightTrigger: 255 };
    default: return x;
  }
}

async function sendGamepadTap(button: string, hold: number): Promise<void> {
  const downX = applyButtonToXInput(button, neutralXInput());

  const base: Omit<PlaycastGamepadXInput, 'xInput'> = {
    gamepadId: 0,
    playerCoordinates: {
      origin: 'bottomLeft',
      dimensions: { x: 0, y: 0 },
      display: { x: 0, y: 0 },
    },
  };

  ipcSend(generators.gamepad.setXInput('host', { ...base, xInput: downX }));
  await delay(hold);

  ipcSend(generators.gamepad.setXInput('host', { ...base, xInput: neutralXInput() }));
  await delay(20);
}

// ========================================
// MacroManager Class
// ========================================

export class MacroManager {
  private static readonly INACTIVE_PERIOD_MS = 30_000;
  private static readonly MAX_JOIN_MACRO_WAIT_MS = 4_000;

  private pavilionCoordinator: PavilionCoordinator;
  private analytics: IHostAnalyticsManager;
  private hasExecutedLaunchMacro = false;
  // Incremented on reset() so an in-flight onLaunchMacro from a closing game
  // cannot mark itself executed after reset() and block the next launch.
  private launchMacroGeneration = 0;
  private inactiveIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(pavilionCoordinator: PavilionCoordinator, analytics: IHostAnalyticsManager) {
    this.pavilionCoordinator = pavilionCoordinator;
    this.analytics = analytics;
  }

  // ========================================
  // Public API
  // ========================================

  /**
   * Execute onLaunchMacro (awaited before host becomes matchmakeable)
   */
  public async executeOnLaunchMacro(): Promise<void> {
    if (this.hasExecutedLaunchMacro) {
      console.log('[MacroManager] onLaunchMacro already executed, skipping');
      return;
    }

    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onLaunchMacro');
      return;
    }

    const { onLaunchMacro } = config;
    if (!onLaunchMacro || onLaunchMacro.length === 0) {
      console.log('[MacroManager] No onLaunchMacro defined for campaign:', config.campaignId);
      return;
    }

    console.log(`[MacroManager] Executing onLaunchMacro for "${config.displayName}":`, onLaunchMacro);

    const generationAtStart = this.launchMacroGeneration;
    let success = false;
    try {
      await this.runMacroSteps(onLaunchMacro, 'Launch');
      if (generationAtStart !== this.launchMacroGeneration) {
        console.log('[MacroManager] onLaunchMacro finished after game closed — not marking as executed');
        return;
      }
      this.hasExecutedLaunchMacro = true;
      success = true;
      console.log('[MacroManager] onLaunchMacro completed successfully');
    } catch (error) {
      console.error('[MacroManager] onLaunchMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onLaunch',
        success,
        campaignId: config.campaignId,
      });
    }
  }

  /**
   * Execute onJoinMacro (awaited before HOST_READY / stream start).
   *
   * Stops the inactive loop first, then races the macro against a timeout cap.
   * If the macro takes longer than MAX_JOIN_MACRO_WAIT_MS, the promise resolves
   * early but the macro continues running in the background.
   */
  public async executeOnJoinMacro(): Promise<void> {
    this.stopInactiveLoop();

    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onJoinMacro');
      return;
    }

    const { onJoinMacro } = config;
    if (!onJoinMacro || onJoinMacro.length === 0) {
      console.log('[MacroManager] No onJoinMacro defined for campaign:', config.campaignId);
      return;
    }

    console.log(`[MacroManager] Executing onJoinMacro for "${config.displayName}":`, onJoinMacro);

    const startTime = Date.now();
    let success = false;
    const macroPromise = this.runMacroSteps(onJoinMacro, 'Player join')
      .then(() => {
        success = true;
        console.log('[MacroManager] onJoinMacro completed successfully');
      })
      .catch((error) => console.error('[MacroManager] onJoinMacro failed:', error))
      .finally(() => {
        this.analytics.sendMacroExecutedEvent({
          macroType: 'onJoin',
          success,
          campaignId: config.campaignId,
        });
      });

    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        console.warn(`[MacroManager] Player join macro still running after ${MacroManager.MAX_JOIN_MACRO_WAIT_MS}ms, proceeding without waiting.`);
        resolve();
      }, MacroManager.MAX_JOIN_MACRO_WAIT_MS);
    });

    await Promise.race([macroPromise, timeoutPromise]);
  }

  /**
   * Execute onExitMacro (awaited before re-entering matchmaking pool).
   *
   * Called when all players leave and restartOnDisconnect is false.
   * After the macro completes, starts the inactive loop.
   */
  public async executeOnExitMacro(): Promise<void> {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onExitMacro');
      this.startInactiveLoop();
      return;
    }

    const { onExitMacro } = config;
    if (!onExitMacro || onExitMacro.length === 0) {
      console.log('[MacroManager] No onExitMacro defined for campaign:', config.campaignId);
      this.startInactiveLoop();
      return;
    }

    console.log(`[MacroManager] Executing onExitMacro for "${config.displayName}":`, onExitMacro);

    const startTime = Date.now();
    let success = false;
    try {
      await this.runMacroSteps(onExitMacro, 'Player Exit');
      success = true;
      console.log('[MacroManager] onExitMacro completed successfully');
    } catch (error) {
      console.error('[MacroManager] onExitMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onExit',
        success,
        campaignId: config.campaignId,
      });
    }

    this.startInactiveLoop();
  }

  /**
   * Execute onInactiveMacro (fire-and-forget from interval timer)
   */
  public async executeOnInactiveMacro(): Promise<void> {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) return;

    const { onInactiveMacro } = config;
    if (!onInactiveMacro || onInactiveMacro.length === 0) return;

    const startTime = Date.now();
    let success = false;
    try {
      await this.runMacroSteps(onInactiveMacro, 'Inactive');
      success = true;
    } catch (error) {
      console.error('[MacroManager] onInactiveMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onInactive',
        success,
        campaignId: config.campaignId,
      });
    }
  }

  /**
   * Stop the inactive macro loop.
   */
  public stopInactiveLoop(): void {
    if (this.inactiveIntervalId) {
      clearInterval(this.inactiveIntervalId);
      this.inactiveIntervalId = null;
      console.log('[MacroManager] Stopped inactive loop');
    }
  }

  /**
   * Reset launch macro tracking and stop inactive loop (called when game closes)
   */
  public reset(): void {
    this.launchMacroGeneration++;
    this.hasExecutedLaunchMacro = false;
    this.stopInactiveLoop();
    console.log('[MacroManager] Reset launch macro tracking');
  }

  // ========================================
  // Private
  // ========================================

  /**
   * Start the inactive macro loop (fires every 30s).
   */
  private startInactiveLoop(): void {
    this.stopInactiveLoop();

    this.inactiveIntervalId = setInterval(() => {
      void this.executeOnInactiveMacro();
    }, MacroManager.INACTIVE_PERIOD_MS);

    console.log('[MacroManager] Started inactive loop (every 30s)');
  }

  /**
   * Run an array of macro step strings sequentially.
   *
   * Public so the StatefulMacroCoordinator can drive per-node macro execution
   * directly, identically to how `executeOnLaunchMacro` etc. drive it
   * internally. The `label` parameter is purely for log labelling and has a
   * neutral default for stateful-macro callers who use their own logging.
   */
  public async runMacroSteps(steps: string[], label = 'Macro'): Promise<void> {
    if (!steps || steps.length === 0) {
      console.log(`[MacroManager] No steps for ${label}, skipping.`);
      return;
    }

    try {
      ipcSend(generators.input.toggleFilters('host', { keyboard: false, mouse: true, gamepad: false }));
      await delay(100);
  
      console.log(`[MacroManager] Running ${label} with ${steps.length} steps.`);
  
      for (const rawStep of steps) {
        const step = parseMacroStep(rawStep);
        if (!step) continue;
  
        if (step.type === 'key') {
          const keyCode = Number(step.value);
          if (Number.isNaN(keyCode)) {
            console.warn('[MacroManager] Invalid key code in step:', rawStep);
            continue;
          }
  
          console.log(`[MacroManager] Sending key press: ${keyCode}`);
          await delay(step.timing);
          await sendKeyPress(keyCode, false, step.hold);
        } else if (step.type === 'string') {
          console.log(`[MacroManager] Sending string: ${step.value}`);
          await delay(step.timing);
          await sendKeyString(step.value);
        } else if (step.type === 'gpad') {
          console.log(`[MacroManager] Sending gpad: ${step.value}`);
          await delay(step.timing);
          await sendGamepadTap(step.value, step.hold);
        }
      }
    } catch (error) {
      console.error('[MacroManager] Failed to run macro steps:', error);
    } finally {
      ipcSend(generators.input.toggleFilters('host', { keyboard: true, mouse: true, gamepad: true }));
    }

    console.log(`[MacroManager] ${label} macro complete.`);
  }
}
